import React from 'react';
import { Button, Column, Row, Stack } from '@grubhubprod/gh-cookbook-react';
import { useForm, FormProvider } from 'react-hook-form/dist/index.ie11';
import { Input } from 'src/components/Input/Input';
import {
  getFormattedPhoneNumber,
  PhoneMaskInput,
  removePhoneNumberFormatting,
} from 'src/components/PhoneMaskInput/PhoneMaskInput';
import { pushErrorFromAPI, pushSuccess } from 'src/utilities/notifications';
import {
  addInvoiceContact,
  InvoiceContactPayload,
  SubtotalInvoiceMethod,
} from 'src/utilities/invoiceContact.utils';
import { InvoiceRecipientAutocomplete } from 'src/components/enterprise/EnterpriseInvoiceRecipients/InvoiceRecipientAutocomplete';
import {
  defaultFormValues,
  InvoiceRecipient,
} from 'src/components/enterprise/EnterpriseInvoiceRecipients/InvoiceRecipientAutocomplete.utils';
import { EMAIL_REGEX, NAME_REGEX } from 'src/utilities/regexPatterns';

interface IEnterpriseInvoiceRecipientModal {
  corpId: string;
  subtotalMethod: SubtotalInvoiceMethod;
  resourceId?: string;
  onSubmitted?: () => void;
}

export interface InvoiceRecipientForm {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
}

export function EnterpriseInvoiceRecipientForm({
  corpId,
  subtotalMethod,
  resourceId,
  onSubmitted,
}: IEnterpriseInvoiceRecipientModal): JSX.Element {
  const useFormProps = useForm<InvoiceRecipientForm>({
    mode: 'all',
    defaultValues: defaultFormValues,
  });
  const { handleSubmit, register, errors, formState, control, reset } =
    useFormProps;
  const { isSubmitting, isValid } = formState;

  async function onFormSubmit({
    firstName,
    lastName,
    email,
    phoneNumber,
  }): Promise<void> {
    const payload: InvoiceContactPayload = {
      first_name: firstName,
      last_name: lastName,
      email,
      phone: removePhoneNumberFormatting(phoneNumber),
    };
    const fullContactName = `${firstName} ${lastName}`;
    try {
      await addInvoiceContact({ corpId, subtotalMethod, resourceId, payload });
      if (onSubmitted) {
        onSubmitted();
      }
      pushSuccess(`${fullContactName} was added`);
      reset(defaultFormValues);
    } catch (error) {
      pushErrorFromAPI(error, {
        fallbackMessage: `${fullContactName} was not added. Try again.`,
      });
    }
  }

  const updateRecipient = ({
    first_name,
    last_name,
    email,
    phone,
  }: InvoiceRecipient) => {
    reset({
      firstName: first_name,
      lastName: last_name,
      email,
      phoneNumber: getFormattedPhoneNumber(phone),
    });
  };

  return (
    <FormProvider {...useFormProps}>
      <Stack y="x-sm">
        <Row gutter={24}>
          <Column gutter={24} phone={3}>
            <Stack y="md">
              <InvoiceRecipientAutocomplete
                name="firstName"
                corpId={corpId}
                onSelect={updateRecipient}
              >
                <Input
                  label="First name *"
                  name="firstName"
                  id="firstName"
                  maxLength={60}
                  ref={register({
                    required: 'First name is required',
                    pattern: {
                      value: NAME_REGEX,
                      message: 'First name is invalid',
                    },
                  })}
                  error={errors.firstName}
                />
              </InvoiceRecipientAutocomplete>
            </Stack>
          </Column>
          <Column gutter={24} phone={3}>
            <Stack y="md">
              <InvoiceRecipientAutocomplete
                name="lastName"
                corpId={corpId}
                onSelect={updateRecipient}
              >
                <Input
                  label="Last name *"
                  name="lastName"
                  id="lastName"
                  maxLength={60}
                  ref={register({
                    required: 'Last name is required',
                    pattern: {
                      value: NAME_REGEX,
                      message: 'Last name is invalid',
                    },
                  })}
                  error={errors.lastName}
                />
              </InvoiceRecipientAutocomplete>
            </Stack>
          </Column>
          <Column gutter={24} phone={3}>
            <Stack y="md">
              <InvoiceRecipientAutocomplete
                name="email"
                corpId={corpId}
                onSelect={updateRecipient}
              >
                <Input
                  label="Email *"
                  name="email"
                  id="email"
                  type="email"
                  placeholder="e.g. admin@company.com"
                  ref={register({
                    required: 'Email is required',
                    pattern: {
                      value: EMAIL_REGEX,
                      message: 'Email is invalid',
                    },
                  })}
                  error={errors.email}
                />
              </InvoiceRecipientAutocomplete>
            </Stack>
          </Column>
          <Column gutter={24} phone={3}>
            <Stack y="md">
              <PhoneMaskInput
                label="Phone *"
                id="phoneNumber"
                name="phoneNumber"
                required
                control={control}
                error={errors.phoneNumber}
              />
            </Stack>
          </Column>
        </Row>
      </Stack>
      <Button
        variant="primary"
        buttonSize="medium"
        disabled={!isValid || isSubmitting}
        isLoading={isSubmitting}
        onClick={handleSubmit(onFormSubmit)}
        type="button"
      >
        Add recipient
      </Button>
    </FormProvider>
  );
}
